import { createBulkActions } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Payments/components/createBulkActions.ts';
import { IncompletePaymentDrawer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Payments/components/IncompletePaymentDrawer.tsx';
import { usePaymentsTable } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Payments/components/usePaymentsTable.tsx';
import { usePermissionRequest } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper.tsx';
import { MantineTable } from '@dealstack/frontend/shared/components/Table/MantineTable/MantineTable.tsx';
import { getTableMeta } from '@dealstack/frontend/shared/components/Table/MantineTable/types.ts';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme.ts';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams.ts';
import {
	DataMipPaymentRes,
	GetPaymentStatisticsRes,
	PermissionRole,
	ResourceType,
	TransactionState,
} from '@dealstack/shared/lib/generated';
import { Flex } from '@mantine/core';
import { Table as TanstackTable } from '@tanstack/react-table';
import { useCallback, useState } from 'react';
import { BulkUpdatePaymentFn, UpdatePaymentFn } from './Table.utils.types';

interface Props {
	payments?: DataMipPaymentRes[];
	summary?: GetPaymentStatisticsRes;
	updatePayment: UpdatePaymentFn;
	bulkUpdatePayment: BulkUpdatePaymentFn;
	onDownloadPayments: () => void;
	isDownloading?: boolean;
	isUpdating?: boolean;
}

type IncompleteDrawerState = {
	ids: string[];
	table?: TanstackTable<DataMipPaymentRes>;
};

const resetBulkActionState = (table?: TanstackTable<DataMipPaymentRes>) => {
	if (!table) {
		return;
	}

	table.resetRowSelection();
	getTableMeta(table).resetActionState?.();
};

export const Table = ({
	payments = [],
	summary,
	updatePayment,
	bulkUpdatePayment,
	onDownloadPayments,
	isDownloading = false,
	isUpdating = false,
}: Props) => {
	const { mipId } = useRequiredParams('mipId');
	const tMipManage = useTranslationMipManage();
	const { hasPermission: hasEditorPermission } = usePermissionRequest({
		resourceType: ResourceType.PROGRAMME,
		resourceId: mipId,
		permissionRole: PermissionRole.EDITOR,
	});
	const [incompleteDrawerState, setIncompleteDrawerState] =
		useState<IncompleteDrawerState | null>(null);
	const [initialAnnotation, setInitialAnnotation] = useState<string | null>(null);

	const openIncompleteDrawer = (
		paymentIds: string[],
		annotation?: string | null,
		table?: TanstackTable<DataMipPaymentRes>
	) => {
		setInitialAnnotation(annotation ?? null);
		setIncompleteDrawerState({
			ids: paymentIds,
			table,
		});
	};

	const closeIncompleteDrawer = () => {
		setIncompleteDrawerState(null);
		setInitialAnnotation(null);
	};

	const handleBulkUpdatePayment = useCallback(
		({
			ids,
			status,
			annotation,
			table,
		}: {
			ids: string[];
			status: TransactionState;
			annotation?: string;
			table?: TanstackTable<DataMipPaymentRes>;
		}) => {
			if (!ids.length) {
				return;
			}

			bulkUpdatePayment(
				{
					ids,
					status,
					annotation,
				},
				{
					onSuccess: () => {
						resetBulkActionState(table);
					},
				}
			);
		},
		[bulkUpdatePayment]
	);

	const { columns, mantineTableDataOperations } = usePaymentsTable({
		payments,
		summary,
		updatePayment,
		onOpenIncompleteDrawer: openIncompleteDrawer,
	});

	const handleSaveIncompleteStatus = (annotation: string) => {
		if (!incompleteDrawerState) {
			return;
		}

		handleBulkUpdatePayment({
			ids: incompleteDrawerState.ids,
			status: TransactionState.INCOMPLETE,
			annotation,
			table: incompleteDrawerState.table,
		});
		closeIncompleteDrawer();
	};

	return (
		<Flex w="100%" style={{ overflow: 'hidden' }}>
			<MantineTable<DataMipPaymentRes>
				getRowId={(row) => row.id}
				columns={columns}
				data={payments}
				dataOperations={mantineTableDataOperations}
				isLoading={false}
				renderBulkActionsMenu={true}
				actions={(table) =>
					createBulkActions(table, {
						isBusy: isUpdating,
						canBulkUpdate: hasEditorPermission,
						isDownloading,
						tMipManage,
						onDownload: onDownloadPayments,
						onBulkUpdate: ({ ids, status, annotation }) =>
							handleBulkUpdatePayment({ ids, status, annotation, table }),
						onOpenIncompleteDrawer: (paymentIds, annotation) =>
							openIncompleteDrawer(paymentIds, annotation, table),
					})
				}
				enableSearchBar={true}
				enableFilters={true}
				enablePagination={true}
				enableColumnResizing={true}
				pinnedColumnIds={['investmentId', 'investor']}
				manualPagination={false}
				manualFiltering={false}
				manualSorting={false}
				enableRowSelection={
					hasEditorPermission ? (row) => !row.isInvestmentWorkflowCompleted : false
				}
				enableRowActions={false}
				enableHiding={true}
				initialState={{ columnVisibility: { investmentId: false, investorId: false } }}
				enableTopToolbar={true}
				enableStickyHeader={true}
				enableStickyFooter={true}
				hideFooterRows={false}
				enableVerticalCellBorders={false}
				footerCellMinHeight="56px"
				footerCellAlignTop={true}
				mantineTableBodyRowProps={({ row }) => {
					const isIncomplete = row.original.paymentStatus === TransactionState.INCOMPLETE;
					const canOpenIncompleteDrawer = isIncomplete && hasEditorPermission;

					return {
						onClick: () => {
							if (canOpenIncompleteDrawer) {
								openIncompleteDrawer(
									[row.original.id],
									row.original.paymentAnnotation
								);
							}
						},
						style: {
							backgroundColor: isIncomplete
								? defaultTheme.v3.colors.red[0]
								: undefined,
							cursor: canOpenIncompleteDrawer ? 'pointer' : 'default',
							height: 'unset',
						},
					};
				}}
			/>

			<IncompletePaymentDrawer
				opened={!!incompleteDrawerState}
				onClose={closeIncompleteDrawer}
				initialAnnotation={initialAnnotation}
				onConfirm={handleSaveIncompleteStatus}
			/>
		</Flex>
	);
};
